import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Chip, EmptyState, Skeleton } from '../ui'
import { ApiError } from '../../api/client'
import { useMappingBoard, useMappingSites, useMappingSummary } from '../../api/hooks'
import type { AccessSystem, MappingItem, MappingSummary } from '../../api/types'

/** Kind vocab from the backend's adoption engine, pluralized for display.
 *  Exported for the workbench's category rail. */
export const kindLabels: Record<string, string> = {
  person: 'Persons',
  credential: 'Credentials',
  device: 'Devices',
  location: 'Locations',
  group: 'Groups',
  zone: 'Zones',
  door: 'Doors',
  door_code: 'Door codes',
  access_group: 'Access groups',
  schedule: 'Schedules',
  new_credential: 'New credentials',
}

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** Detail-page route per kind for a BOUND row's canonical outcome (kinds
 *  without a detail page — doors, locations, schedules — stay unlinked). */
const OUTCOME_ROUTES: Record<string, string> = {
  person: '/directory',
  credential: '/credentials',
  group: '/groups',
  access_group: '/access-groups',
}

/** A bound row's display links to its canonical entity's detail page. */
function outcomeLink(item: MappingItem): string | null {
  if (item.state !== 'bound' || !item.outcome) return null
  const base = OUTCOME_ROUTES[item.kind]
  return base ? `${base}/${item.outcome}` : null
}

/** Per-kind count line, e.g. `3 undecided · 1 bound · 2 skipped`. */
function countLine(counts: MappingSummary): string {
  const parts = [`${counts.undecided} undecided`, `${counts.bound} bound`]
  if (counts.skipped > 0) parts.push(`${counts.skipped} skipped`)
  return parts.join(' · ')
}

/** "How matching works" — the operator-facing parsing rules, ONE list kept
 *  in sync with the backend's matchers (address hints, identifier prefixes,
 *  door-kind detection, trigram fallback). Rendered default-collapsed. */
const MATCHING_RULES: string[] = [
  'Reader names like "23 – Armborstv. 12 …" (NN – street no) hint the address from the street token.',
  '"Lgh <digits>" in a name matches the building whose Location identifier prefixes the digits (longest wins), and the Lgh NNNN apartment node (last 4 digits) under it.',
  'CAME contact descriptions like "Sandaletten 2-51203" (fastighet-number) match the apartment by its last 4 digits.',
  'Location identifiers are operator-declared, space-separated prefixes (internal / Skatteverket / port numbers) — set them on the Locations page.',
  'Door types: PB… → Mailbox, sug/sopor/trash → Trash, everything else → Door — overridable per door and at decide time.',
  'Everything else is trigram name similarity — the % rating shown on each hint.',
]

/**
 * The mapping guide's per-kind summary for one System. The summary endpoint
 * 400s (capability_not_supported) for providers whose connector has no
 * SupportsAdoption — that is a provider verdict, rendered as a dedicated
 * empty state rather than an error box (the reads never retry it).
 */
export function MappingCard({ system, onOpen }: { system: AccessSystem; onOpen?: () => void }) {
  const summary = useMappingSummary(system.unique_id)
  // The per-kind rows expand to show WHAT the candidates are, not just how
  // many — the board (the full item lists) is fetched lazily on first
  // expand, never on card render.
  const [expanded, setExpanded] = useState<string | null>(null)
  const [rulesOpen, setRulesOpen] = useState(false)
  const board = useMappingBoard(expanded !== null ? system.unique_id : undefined)
  // Multi-site providers report per-site progress (a 400 verdict or [] =
  // single-site, nothing extra rendered; a ONE-site list adds nothing over
  // the total chip, so the block only renders for 2+ sites).
  const sites = useMappingSites(system.unique_id)
  const siteList = sites.data ?? []

  const entries = Object.entries(summary.data ?? {})
  const totalUndecided = entries.reduce((sum, [, counts]) => sum + counts.undecided, 0)

  return (
    <Card
      title="Mapping"
      action={
        <span className="flex items-center gap-2">
          {summary.data && totalUndecided > 0 && <Chip tone="warn" label={`${totalUndecided} to map`} />}
          {onOpen && (
            <Button variant="ghost" size="sm" onClick={onOpen}>
              Workbench
            </Button>
          )}
        </span>
      }
    >
      {summary.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : summary.isError ? (
        summary.error instanceof ApiError && summary.error.status === 400 ? (
          <EmptyState
            title="Mapping not supported"
            hint="This provider doesn't support mapping — there is nothing to decide."
          />
        ) : (
          <EmptyState title="Could not load mapping summary" hint={errDetail(summary.error)} />
        )
      ) : entries.every(([, c]) => c.undecided + c.skipped + c.bound === 0) ? (
        <EmptyState
          title="Nothing to map"
          hint="This system exposes no unmappable objects."
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">
          {entries.map(([kind, counts]) => {
            const isOpen = expanded === kind
            const items = isOpen ? (board.data?.[kind] ?? []) : []
            return (
              <li key={kind} className="rounded-sm border border-ostrel-line bg-ostrel-card">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={`${kindLabels[kind] ?? kind} items`}
                  onClick={() => setExpanded(isOpen ? null : kind)}
                  className="flex w-full flex-wrap items-center gap-2 px-3.5 py-3 text-left transition-colors hover:bg-ostrel-sand/60 focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                >
                  <span className="text-sm font-medium text-ostrel-ink">{kindLabels[kind] ?? kind}</span>
                  <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                    {countLine(counts)}
                  </span>
                  <span
                    className={`text-xs text-ostrel-ink-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  >
                    ▾
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-ostrel-line px-3.5 py-2">
                    {board.isPending ? (
                      <Skeleton className="h-6" />
                    ) : board.isError ? (
                      <p className="text-[11px] text-ostrel-danger">{errDetail(board.error)}</p>
                    ) : items.length === 0 ? (
                      <p className="text-[11px] text-ostrel-ink-muted">No items.</p>
                    ) : (
                      <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
                        {items.map((item) => (
                          <li key={item.vendor_uid} className="flex items-center gap-2 text-[11px]">
                            {outcomeLink(item) ? (
                              <Link
                                to={outcomeLink(item) as string}
                                className="tnum min-w-0 flex-1 truncate text-ostrel-ink hover:text-ostrel-accent"
                              >
                                {item.display}
                              </Link>
                            ) : (
                              <span className="tnum min-w-0 flex-1 truncate text-ostrel-ink">{item.display}</span>
                            )}
                            <span
                              className={`shrink-0 text-[10px] font-semibold uppercase tracking-widest ${
                                item.state === 'undecided'
                                  ? 'text-ostrel-gold'
                                  : item.state === 'bound'
                                    ? 'text-ostrel-ok'
                                    : 'text-ostrel-ink-muted'
                              }`}
                            >
                              {item.state}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            )
          })}
          </ul>
          {/* Per-site progress only earns its rows when there IS more than
              one site — a single row would just duplicate the header chip. */}
          {siteList.length > 1 && (
            <ul
              aria-label="Per-site progress"
              className="mt-2 flex flex-col gap-1 border-t border-ostrel-line pt-2"
            >
              {siteList.map((s) => (
                <li key={s.site_key} className="flex items-center gap-2 text-[11px]">
                  <span className="min-w-0 flex-1 truncate text-ostrel-ink">{s.label}</span>
                  <span
                    className={`tnum shrink-0 ${s.undecided > 0 ? 'text-ostrel-gold' : 'text-ostrel-ok'}`}
                  >
                    {s.undecided > 0 ? `${s.undecided} to map` : 'done'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* The parsing rules, default-collapsed — how vendor names become
              locations/kinds, in plain language. */}
          <div className="mt-2 border-t border-ostrel-line pt-2">
            <button
              type="button"
              aria-expanded={rulesOpen}
              onClick={() => setRulesOpen((v) => !v)}
              className="flex w-full items-center gap-2 text-left"
            >
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                How matching works
              </span>
              <span className="ml-auto text-[11px] text-ostrel-ink-muted">
                {rulesOpen ? 'Hide ▴' : 'Show ▾'}
              </span>
            </button>
            {rulesOpen && (
              <ul className="mt-2 flex flex-col gap-1.5">
                {MATCHING_RULES.map((rule) => (
                  <li key={rule} className="flex gap-1.5 text-[11px] leading-relaxed text-ostrel-ink-muted">
                    <span className="shrink-0">•</span>
                    {rule}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Card>
  )
}
